import { PhoneNumberType } from '@/models';
import { CFSEvent } from '@/models/cfs';

import {
  FALLBACK,
  formatAddress,
  formatAddressCityStateZip,
  formatAssignedAgencies,
  formatCallerId,
  formatTimelineEventDate,
  formatTimelineEventTime,
  formatContactPhone,
  formatCrossStreet,
  formatDescription,
  formatDisposition,
  formatIncidentCodes,
  formatNarrativeCreatedBy,
  formatNarrativeDate,
  formatPersonAddress,
  formatPersonAge,
  formatPersonCityStateZip,
  formatPersonDOB,
  formatPersonName,
  formatPersonPhoneByType,
  formatPersonRace,
  formatPersonRole,
  formatPersonSex,
  formatPersonType,
  formatReceivedBy,
  formatReporterAddress,
  formatReporterCityStateZip,
  formatReporterName,
  formatReporterSubAddress,
  formatSeverity,
  formatSubAddress,
  formatUnitEmployees,
  formatUnitEquipment,
  formatUnitName,
  formatUnitStatus,
  formatUnitType,
  formatUnitVehicles,
  formatVehicleColor,
  formatVehicleMake,
  formatVehicleModel,
  formatVehicleOwner,
  formatVehiclePlate,
  formatVehiclePlateState,
  formatVehicleYear,
  safeText,
} from './mappers';
import type { CFSReportSectionDef } from './types';

/**
 * Top section fields (page 1 only, no title card).
 * Each field includes a `width` so the builder can produce variable-width columns.
 */
export const cfsTopSectionFields: CFSReportSectionDef = {
  key: 'topSection',
  title: '',
  detail: {
    columnsPerRow: 5,
    fields: [
      {
        key: 'entryCC',
        label: 'Entry / CC#',
        value: (ctx) => safeText(ctx.cfs.number),
        width: 120,
      },
      {
        key: 'date',
        label: 'Date',
        value: (ctx) =>
          formatTimelineEventDate(
            ctx.cfsTimelineLogs,
            CFSEvent.CREATED,
            ctx.cfs.createdAt,
          ),
        width: 100,
      },
      {
        key: 'time',
        label: 'Time',
        value: (ctx) =>
          formatTimelineEventTime(
            ctx.cfsTimelineLogs,
            CFSEvent.CREATED,
            ctx.cfs.createdAt,
          ),
        width: 80,
      },
      {
        key: 'tour',
        label: 'Tour',
        value: () => FALLBACK,
        width: 80,
      },
      {
        key: 'deskOfficer',
        label: 'Desk Officer',
        value: (ctx) => safeText(ctx.cfs.createdBy?.fullName),
        width: '*',
      },
      // Row 2
      {
        key: 'callType',
        label: 'Call Type',
        value: () => FALLBACK,
        width: 120,
        colSpan: 3,
      },
      {
        key: 'priority',
        label: 'Priority',
        value: (ctx) => formatSeverity(ctx.cfs.severity),
        width: 80,
      },
      {
        key: 'howReceived',
        label: 'How Received',
        value: (ctx) => formatReceivedBy(ctx.cfs.reporter?.receivedBy),
        width: '*',
      },
    ],
  },
};

/**
 * Main CFS report sections (each rendered as a titled card).
 */
export const cfsReportSections: CFSReportSectionDef[] = [
  {
    key: 'cfsDetails',
    title: 'CFS DETAILS',
    detail: {
      columnsPerRow: 12,
      fields: [
        {
          key: 'address',
          label: 'Address',
          value: (ctx) => formatAddress(ctx.cfs.addressInfo),
          width: '*',
          colSpan: 5,
        },
        {
          key: 'cityStateZip',
          label: 'City / State / ZIP',
          value: (ctx) => formatAddressCityStateZip(ctx.cfs.addressInfo),
          width: '*',
          colSpan: 4,
        },
        {
          key: 'subAddress',
          label: 'Sub-address',
          value: (ctx) => formatSubAddress(ctx.cfs.addressInfo),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'instructions',
          label: 'Instructions',
          value: (ctx) => safeText(ctx.cfs.addressInfo?.instructions),
          width: '*',
          colSpan: 12,
        },
        {
          key: 'locationOfAssignment',
          label: 'Location of Assignment',
          value: () => FALLBACK,
          width: '*',
          colSpan: 5,
        },
        {
          key: 'crossStreet',
          label: 'Cross street',
          value: (ctx) => formatCrossStreet(ctx.cfs.addressInfo),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'businessName',
          label: 'Business Name',
          value: (ctx) =>
            safeText(
              ctx.cfs.addressInfo?.businessName ??
                ctx.cfs.addressInfo?.address?.businessName,
            ),
          width: '*',
          colSpan: 4,
        },
        {
          key: 'incidentCode',
          label: 'Incident Code',
          value: (ctx) => formatIncidentCodes(ctx.cfs.incidentCodes),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'severity',
          label: 'Severity',
          value: (ctx) => formatSeverity(ctx.cfs.severity),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'shortDescription',
          label: 'Short description',
          value: (ctx) => safeText(ctx.cfs.shortDescription),
          width: '*',
          colSpan: 7,
        },
        {
          key: 'disposition',
          label: 'Disposition',
          value: (ctx) =>
            formatDisposition(ctx.cfs.closeReason, ctx.cfsCloseReasons),
          width: '*',
          colSpan: 4,
        },
        {
          key: 'agencies',
          label: 'Agencies',
          value: (ctx) =>
            formatAssignedAgencies(
              ctx.cfs.assignedAgencies as any,
              ctx.agencies,
            ),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'description',
          label: 'Description',
          value: (ctx) => formatDescription(ctx.cfs.additionalInfo),
          width: '*',
          colSpan: 5,
        },
        {
          key: 'callerId',
          label: 'Caller ID',
          value: (ctx) => formatCallerId(ctx.cfs, ctx.cfsTimelineLogs),
          width: '*',
          colSpan: 12,
        },
      ],
    },
  },
  {
    key: 'reporterInformation',
    title: 'REPORTER INFORMATION',
    detail: {
      columnsPerRow: 6,
      fields: [
        {
          key: 'name',
          label: 'Name',
          value: (ctx) => formatReporterName(ctx.cfs.primaryReporter),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'contactPhone',
          label: 'Contact phone number',
          value: (ctx) => formatContactPhone(ctx.cfs.reporter),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'address',
          label: 'Address',
          value: (ctx) => formatReporterAddress(ctx.cfs.primaryReporter),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'cityStateZip',
          label: 'City / State / ZIP',
          value: (ctx) => formatReporterCityStateZip(ctx.cfs.primaryReporter),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'subAddress',
          label: 'Sub-address',
          value: (ctx) => formatReporterSubAddress(ctx.cfs.primaryReporter),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'post',
          label: 'Post',
          value: () => FALLBACK,
          width: '*',
          colSpan: 2,
        },
        {
          key: 'dispatched',
          label: 'Dispatched',
          value: () => FALLBACK,
          width: '*',
          colSpan: 2,
        },
        {
          key: 'dispatchedDate',
          label: 'Dispatched Date',
          value: () => FALLBACK,
          width: '*',
          colSpan: 2,
        },
        {
          key: 'callTaker',
          label: 'Call Taker',
          value: () => FALLBACK,
          width: '*',
          colSpan: 6,
        },
      ],
    },
  },
  {
    key: 'units',
    title: 'UNITS',
    items: (ctx) => ctx.cfs.assignedUnitShifts || [],
    detail: {
      columnsPerRow: 6,
      fields: [
        {
          key: 'unitName',
          label: 'Unit name',
          value: (_ctx, u) => formatUnitName(u),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'unitType',
          label: 'Unit type',
          value: (_ctx, u) => formatUnitType(u),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'status',
          label: 'Status',
          value: (_ctx, u) => formatUnitStatus(u),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'employees',
          label: 'Employees',
          value: (_ctx, u) => formatUnitEmployees(u),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'vehicles',
          label: 'Vehicles',
          value: (_ctx, u) => formatUnitVehicles(u),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'equipment',
          label: 'Equipment',
          value: (_ctx, u) => formatUnitEquipment(u),
          width: '*',
          colSpan: 2,
        },
      ],
    },
  },
  {
    key: 'names',
    title: 'NAMES',
    items: (ctx) => ctx.cfsNames || [],
    detail: {
      columnsPerRow: 12,
      fields: [
        // Row 1: Name (4), Role (2)
        {
          key: 'name',
          label: 'Name',
          value: (_ctx, n) => formatPersonName(n),
          width: '*',
          colSpan: 6,
        },
        {
          key: 'role',
          label: 'Role',
          value: (_ctx, n) => formatPersonRole(n),
          width: '*',
          colSpan: 6,
        },
        // Row 2: Person type (2), Sex (2), Race (2), DOB (2), Age (2)
        {
          key: 'personType',
          label: 'Person type',
          value: (_ctx, n) => formatPersonType(n),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'sex',
          label: 'Sex',
          value: (_ctx, n) => formatPersonSex(n),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'race',
          label: 'Race',
          value: (_ctx, n) => formatPersonRace(n),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'dob',
          label: 'DOB',
          value: (_ctx, n) => formatPersonDOB(n),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'age',
          label: 'Age',
          value: (_ctx, n) => formatPersonAge(n),
          width: '*',
          colSpan: 2,
        },
        // Row 3: Address + City / State / ZIP
        {
          key: 'address',
          label: 'Address',
          value: (_ctx, n) => formatPersonAddress(n),
          width: '*',
          colSpan: 6,
        },
        {
          key: 'cityStateZip',
          label: 'City / State / ZIP',
          value: (_ctx, n) => formatPersonCityStateZip(n),
          width: '*',
          colSpan: 6,
          // Transient persons have no current address — omit this field so the
          // empty "City / State / ZIP" label doesn't render (Address shows "Transient").
          hidden: (_ctx, n) => !!n?.masterName?.transient,
        },
        // Row 4: phone numbers by type
        {
          key: 'phoneHome',
          label: 'Home',
          value: (_ctx, n) => formatPersonPhoneByType(n, PhoneNumberType.HOME),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'phoneMobile',
          label: 'Mobile',
          value: (_ctx, n) =>
            formatPersonPhoneByType(n, PhoneNumberType.MOBILE),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'phoneWork',
          label: 'Work',
          value: (_ctx, n) => formatPersonPhoneByType(n, PhoneNumberType.WORK),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'phoneCaller',
          label: 'Caller',
          value: (_ctx, n) =>
            formatPersonPhoneByType(n, PhoneNumberType.CALLER),
          width: '*',
          colSpan: 3,
        },
        {
          key: 'phoneOther',
          label: 'Other',
          value: (_ctx, n) => formatPersonPhoneByType(n, PhoneNumberType.OTHER),
          width: '*',
          colSpan: 12,
        },
      ],
    },
  },
  {
    key: 'vehicles',
    title: 'VEHICLES',
    items: (ctx) => ctx.cfsVehicles || [],
    detail: {
      columnsPerRow: 10,
      fields: [
        // Row 1: Make, Model, Year, Color, Owner (1 each)
        {
          key: 'make',
          label: 'Make',
          value: (_ctx, v) => formatVehicleMake(v),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'model',
          label: 'Model',
          value: (_ctx, v) => formatVehicleModel(v),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'year',
          label: 'Year',
          value: (_ctx, v) => formatVehicleYear(v),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'color',
          label: 'Color',
          value: (_ctx, v) => formatVehicleColor(v),
          width: '*',
          colSpan: 2,
        },
        {
          key: 'owner',
          label: 'Owner',
          value: (_ctx, v) => formatVehicleOwner(v),
          width: '*',
          colSpan: 2,
        },
        // Row 2: Plate number (3), Plate state (2)
        {
          key: 'plateNumber',
          label: 'Plate number',
          value: (_ctx, v) => formatVehiclePlate(v),
          width: '*',
          colSpan: 5,
        },
        {
          key: 'plateState',
          label: 'Plate state',
          value: (_ctx, v) => formatVehiclePlateState(v),
          width: '*',
          colSpan: 5,
        },
      ],
    },
  },
  {
    key: 'narrative',
    title: 'NARRATIVE',
    items: (ctx) => {
      const all = ctx.narratives || [];
      const primary = all.find((n: any) => n.type === 'Primary');
      const additional = all
        .filter((n: any) => n.type !== 'Primary')
        .sort((a: any, b: any) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });
      return primary ? [primary, ...additional] : additional;
    },
    detail: {
      columnsPerRow: 3,
      fields: [
        // Row 1: Narrative type, Created by, Created at
        {
          key: 'narrativeType',
          label: 'Narrative type',
          value: (_ctx, n) => safeText(n?.type),
          width: '*',
        },
        {
          key: 'createdBy',
          label: 'Created by',
          value: (_ctx, n) => formatNarrativeCreatedBy(n),
          width: '*',
        },
        {
          key: 'createdAt',
          label: 'Created at',
          value: (_ctx, n) => formatNarrativeDate(n?.createdAt),
          width: '*',
        },
        // Row 2: Narrative text (full width) — sentinel triggers rich-text rendering
        {
          key: 'narrative',
          label: 'Narrative',
          value: (_ctx, n) => ({ _narrativeContent: n } as any),
          width: '*',
          colSpan: 3,
        },
      ],
    },
  },
];
